import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Plus, Send, RotateCcw, Download, MessageSquare, Trash2 } from 'lucide-react';
import { sendMessage, clearHistory, newConversation, getHistory, getConversations, deleteConversation } from '../../../redux/AIChatSlice/aiChatSlice';
import JobResultCard from '../../../components/AIAssistant/JobResultCard';
import MarkdownLite from '../../../components/AIAssistant/MarkdownLite';
import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';

/* ── helpers ─────────────────────────────────────────────────────────── */
const titleFromMessages = (msgs) => {
  const first = msgs?.find((m) => m.role === 'user');
  const t = first?.content || '';
  return t.length > 40 ? t.slice(0, 40) + '…' : t || 'New conversation';
};

const truncateTitle = (t = '') => (t.length > 40 ? t.slice(0, 40) + '…' : t || 'New conversation');

const relTime = (ts) => {
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 3600000) return `${Math.max(1, Math.floor(diff / 60000))}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 7 * 86400000) return `${Math.floor(diff / 86400000)}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const QUICK_ACTIONS = [
  { label: 'Find jobs I can apply to this week', msg: 'Find jobs I can apply to this week' },
  { label: 'Best referral opportunities', msg: 'What are my best referral opportunities?' },
  { label: 'Give me a 3-step action plan', msg: 'Give me a 3-step action plan for my job search' },
  { label: 'What should I improve for better fit?', msg: 'What should I improve for better fit?' },
];

const COMPOSER_CHIPS = [
  { label: '✨ Improve match score', msg: 'How can I improve my job match score?' },
  { label: '📝 Draft cover letter', msg: 'Draft a cover letter for my top job match' },
  { label: '💬 Outreach message', msg: 'Write an outreach message to a recruiter' },
  { label: '🎯 Negotiation tips', msg: 'Give me salary negotiation tips' },
];

/* ── message bubbles ─────────────────────────────────────────────────── */
function AIBubble({ children }) {
  return (
    <div className="flex gap-2.5 items-start">
      <div
        className="w-[30px] h-[30px] rounded-[9px] flex-shrink-0 flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg,#3F57E0,#8B5CF6)' }}
      >
        <Sparkles size={14} color="white" />
      </div>
      <div
        className="max-w-[75%] bg-white border border-[#E6E8EE] rounded-[14px] rounded-tl-[4px] px-[14px] py-3 text-[13px] leading-[1.55] text-[#334155] font-dmsans"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
      >
        {children}
      </div>
    </div>
  );
}

function UserBubble({ children }) {
  return (
    <div className="flex justify-end">
      <div
        className="max-w-[70%] text-white px-[14px] py-[10px] rounded-[14px] rounded-br-[4px] text-[13px] leading-[1.5] font-medium font-dmsans"
        style={{ background: '#3F57E0' }}
      >
        {children}
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="flex gap-2.5 items-start">
      <div
        className="w-[30px] h-[30px] rounded-[9px] flex-shrink-0 flex items-center justify-center opacity-60"
        style={{ background: 'linear-gradient(135deg,#3F57E0,#8B5CF6)' }}
      >
        <Sparkles size={14} color="white" />
      </div>
      <div
        className="bg-white border border-[#E6E8EE] rounded-[14px] rounded-tl-[4px] px-4 py-3"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
      >
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

/* ── composer ────────────────────────────────────────────────────────────
   Owns the input state so typing re-renders only this component, not the
   whole message list. Input stays enabled while the AI responds; only Send
   is gated. */
const Composer = React.memo(function Composer({ onSend, isLoading, error, inputRef, onRetry }) {
  const [input, setInput] = useState('');

  const send = () => {
    const msg = input.trim();
    if (!msg || isLoading) return;
    onSend(msg);
    setInput('');
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="flex-shrink-0 border-t border-[#EFF1F5] px-4 py-3" style={{ background: '#FAFBFD' }}>
      {/* quick action chips */}
      <div className="flex flex-wrap gap-1.5 mb-2.5">
        {COMPOSER_CHIPS.map(({ label, msg }) => (
          <button
            key={label}
            type="button"
            onClick={() => onSend(msg)}
            disabled={isLoading}
            className="inline-flex items-center px-2.5 py-1 rounded-full border border-[#E6E8EE] bg-white text-[11.5px] font-medium font-dmsans text-[#334155] hover:bg-[#EEF1FE] hover:text-[#3F57E0] hover:border-[#C7D0F8] transition-colors disabled:opacity-50 cursor-pointer"
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12px] font-medium font-dmsans text-red-600 flex items-center justify-between gap-3">
          <span className="min-w-0">{error}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={isLoading}
              className="flex-shrink-0 px-2.5 py-1 rounded-md border border-red-300 bg-white text-[11.5px] font-semibold text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {/* input row */}
      <div className="flex items-center gap-2.5 bg-white border border-[#E6E8EE] rounded-xl px-3 py-2.5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <Sparkles size={15} color="#3F57E0" className="flex-shrink-0" />
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask Champ anything about your job search…"
          className="flex-1 min-w-0 border-0 outline-none text-[13px] font-dmsans text-[#0F172A] bg-transparent placeholder:text-[#94A3B8]"
        />
        <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium font-dmsans text-[#94A3B8] border border-[#E6E8EE] bg-[#F8FAFC] flex-shrink-0">
          ⏎
        </kbd>
        <button
          type="button"
          onClick={send}
          disabled={!input.trim() || isLoading}
          className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold font-dmsans text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          style={{ background: '#3F57E0' }}
        >
          {isLoading
            ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            : <><Send size={11} /> Send</>
          }
        </button>
      </div>
    </div>
  );
});

/* ── main page ───────────────────────────────────────────────────────── */
const AIAssistant = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { messages, isLoading, error, conversationId, conversations, streamingText, followUpQuestion, lastFailedMessage } = useSelector((s) => s.aiChat);
  const user = useSelector((s) => s.auth.user);
  const resumeData = useSelector((s) => s.resume.resumeData);

  const [toast, setToast] = useState(null);
  const [showEraseModal, setShowEraseModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, title }
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevMessagesLenRef = useRef(0);

  /* load conversation list for the history panel */
  useEffect(() => { dispatch(getConversations()); }, [dispatch]);

  /* scroll to bottom on new message or while streaming */
  useEffect(() => {
    if (messages.length !== prevMessagesLenRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      prevMessagesLenRef.current = messages.length;
    } else if (streamingText) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages, streamingText]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleNewConversation = () => {
    dispatch(newConversation());
    inputRef.current?.focus();
  };

  const handleLoadConvo = (c) => {
    setShowHistoryDrawer(false);
    if (c.conversationId === conversationId || isLoading) return;
    dispatch(getHistory(c.conversationId));
  };

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    dispatch(deleteConversation(deleteConfirm.id));
    setDeleteConfirm(null);
  };

  const handleDownload = () => {
    if (!messages.length) return;

    const fmtSalary = (s) => {
      if (!s || (!s.min && !s.max)) return '';
      if (s.min && s.max) return ` | $${s.min.toLocaleString()}–$${s.max.toLocaleString()}`;
      return s.min ? ` | from $${s.min.toLocaleString()}` : ` | up to $${s.max.toLocaleString()}`;
    };

    const blocks = messages.map((m) => {
      const who = m.role === 'user' ? 'You' : 'Champ AI';
      let block = `[${who}]\n${m.content || ''}`;
      const jobs = m.metadata?.jobs || [];
      if (jobs.length > 0) {
        const jobLines = jobs.map((j, i) =>
          `  ${i + 1}. ${j.title || 'Untitled role'} at ${j.company || 'Unknown company'} — ${j.location || 'N/A'}${fmtSalary(j.salary)}`
        );
        block += `\n\nJobs shown:\n${jobLines.join('\n')}`;
      }
      return block;
    });

    const title = titleFromMessages(messages);
    const text = `Champ AI conversation — ${title}\nExported ${new Date().toLocaleString()}\n\n${blocks.join('\n\n---\n\n')}\n`;

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `champ-ai-${title.slice(0, 30).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'conversation'}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    setToast('Conversation downloaded');
    setTimeout(() => setToast(null), 2000);
  };

  const handleSend = useCallback((msg) => {
    dispatch(sendMessage({ message: msg }));
  }, [dispatch]);

  const handleQuick = (action) => {
    if (isLoading) return;
    dispatch(sendMessage({ message: action, conversationId }));
  };

  const handleJobClick = (job) => {
    const id = job?._id || job?.id;
    if (id) {
      // Job detail page fetches the full doc by id; passing the card's job
      // via state lets it render instantly while that loads.
      navigate(`/jobs/${id}`, { state: { job } });
    } else {
      navigate('/', { state: { chatTargetJob: job } });
    }
  };

  const handleCompanyClick = (name, action) => {
    if (!name || isLoading) return;
    const msg = action === 'find_recruiters' ? `Find recruiters at ${name}` : `Analyze hiring signals for ${name}`;
    dispatch(sendMessage({ message: msg, conversationId }));
  };

  const convoTitle = messages.length > 0 ? titleFromMessages(messages) : null;

  /* group past conversations by recency */
  const now = Date.now();
  const ageMs = (c) => now - new Date(c.lastMessageAt).getTime();
  const todayConvos = conversations.filter((c) => ageMs(c) < 86400000);
  const weekConvos = conversations.filter((c) => ageMs(c) >= 86400000 && ageMs(c) < 7 * 86400000);
  const olderConvos = conversations.filter((c) => ageMs(c) >= 7 * 86400000);

  const renderConvoItem = (c) => {
    const isActive = c.conversationId === conversationId;
    return (
      <div
        key={c.conversationId}
        onClick={() => handleLoadConvo(c)}
        className={`group relative flex items-start gap-2.5 px-2.5 py-2.5 rounded-[10px] cursor-pointer transition-colors ${isActive ? '' : 'hover:bg-[#F4F5F8]'}`}
        style={{ background: isActive ? '#EEF1FE' : undefined }}
        title={c.preview}
      >
        {isActive && <div className="w-[3px] self-stretch rounded-full flex-shrink-0" style={{ background: '#3F57E0' }} />}
        <div className="flex-1 min-w-0 text-left">
          <p className={`text-[12px] font-dmsans leading-snug truncate text-left ${isActive ? 'font-semibold' : 'font-medium'}`} style={{ color: isActive ? '#3F57E0' : '#334155' }}>
            {truncateTitle(c.preview)}
          </p>
          <p className="text-[10.5px] text-[#94A3B8] font-dmsans mt-0.5 text-left">{relTime(c.lastMessageAt)}</p>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ id: c.conversationId, title: truncateTitle(c.preview) }); }}
          className="opacity-0 group-hover:opacity-100 flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md hover:bg-red-50 transition-all mt-0.5"
          title="Delete"
        >
          <Trash2 size={14} className="text-red-400" />
        </button>
      </div>
    );
  };

  const renderConvoGroup = (label, convos, extraTopPad = false) => convos.length > 0 && (
    <>
      <div className={`text-[9.5px] font-bold uppercase tracking-[0.1em] text-[#94A3B8] px-2 ${extraTopPad ? 'pt-3' : 'pt-1'} pb-1.5 font-dmsans text-left`}>{label}</div>
      {convos.map(renderConvoItem)}
    </>
  );

  const historyPanelContent = (
    <>
      <div className="px-4 py-3 border-b border-[#E6E8EE] flex-shrink-0 flex items-center gap-2">
        <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: '#EEF1FE' }}>
          <MessageSquare size={12} color="#3F57E0" />
        </div>
        <span className="text-[13px] font-bold text-[#0F172A] font-dmsans">History</span>
        {conversations.length > 0 && (
          <span className="text-[10px] font-bold font-dmsans px-1.5 py-0.5 rounded-full" style={{ background: '#EEF1FE', color: '#3F57E0' }}>
            {conversations.length}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-0.5">
        {renderConvoGroup('Today', todayConvos)}
        {renderConvoGroup('This week', weekConvos, todayConvos.length > 0)}
        {renderConvoGroup('Earlier', olderConvos, todayConvos.length > 0 || weekConvos.length > 0)}

        {conversations.length === 0 && (
          <div className="flex flex-col items-center justify-center flex-1 gap-3 py-12 text-center px-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#EEF1FE,#F0EDFF)' }}>
              <MessageSquare size={16} color="#3F57E0" />
            </div>
            <div>
              <p className="text-[12px] font-semibold text-[#334155] font-dmsans">No conversations yet</p>
              <p className="text-[11px] text-[#94A3B8] font-dmsans mt-0.5">Start a chat to see your history here</p>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const contextChips = [
    user?.jobTitle?.[0] || user?.currentTitle,
    user?.preferredIndustry?.[0],
    user?.skills?.[0],
    resumeData ? 'Resume loaded' : null,
  ].filter(Boolean);

  return (
    <div className="h-screen flex flex-col overflow-hidden font-dmsans" style={{ background: '#F6F7FB' }}>
      <div className="fixed top-0 left-0 right-0 z-50"><Navbaar /></div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] px-5 py-2.5 rounded-xl bg-[#0f172a] text-white text-[13px] font-semibold shadow-xl pointer-events-none">
          {toast}
        </div>
      )}

      {/* Erase confirmation modal */}
      {showEraseModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowEraseModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] max-w-[380px] p-6 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                <RotateCcw size={16} className="text-red-500" />
              </div>
              <div>
                <p className="text-[15px] font-bold text-[#0f172a] font-dmsans">Erase conversation?</p>
                <p className="text-[12.5px] text-gray-400 font-dmsans mt-0.5">This will clear the current conversation history.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-1">
              <button
                onClick={() => setShowEraseModal(false)}
                className="flex-1 py-2 rounded-xl border border-[#E6E8EE] text-[13px] font-semibold text-[#64748b] hover:bg-gray-50 transition-colors font-dmsans"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (conversationId) dispatch(clearHistory(conversationId));
                  setShowEraseModal(false);
                }}
                className="flex-1 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-[13px] font-semibold transition-colors font-dmsans"
              >
                Erase
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 min-h-0 pt-16">
        {/* App sidebar */}
        <div className="hidden sm:flex flex-col w-64 flex-shrink-0 fixed top-16 bottom-0 left-0 z-40 border-r border-[#E6E8EE] bg-white">
          <Sidebar />
        </div>

        {/* Page content */}
        <div className="flex-1 sm:ml-64 flex min-h-0 gap-[18px] p-[18px] min-w-0">

          {/* ── Conversation history panel (desktop) ─────────────────── */}
          <div className="hidden lg:flex flex-col w-[290px] flex-shrink-0 rounded-xl border border-[#E6E8EE] bg-white overflow-hidden">
            {historyPanelContent}
          </div>

          {/* ── Chat panel ─────────────────────────────────────────── */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0 rounded-xl border border-[#E6E8EE] bg-white overflow-hidden">

            {/* header */}
            <div className="flex-shrink-0 flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[#EFF1F5]">
              <div className="text-left min-w-0">
                <div className="text-[14px] font-bold text-[#0F172A] font-dmsans leading-tight">
                  {convoTitle || 'AI Assistant'}
                </div>
                <div className="text-[11.5px] text-[#94A3B8] font-dmsans mt-0.5">
                  {isLoading ? 'Thinking…' : 'Your career copilot · powered by Champ AI'}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowHistoryDrawer(true)}
                  className="lg:hidden flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E6E8EE] bg-white text-[11.5px] font-medium font-dmsans text-[#334155] hover:bg-[#F4F5F8] transition-colors"
                  title="Conversation history"
                >
                  <MessageSquare size={11} /> History
                </button>
                {messages.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E6E8EE] bg-white text-[11.5px] font-medium font-dmsans text-[#334155] hover:bg-[#F4F5F8] transition-colors"
                      title="Download conversation"
                    >
                      <Download size={11} /> Download
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEraseModal(true)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-200 bg-red-50 text-[11.5px] font-medium font-dmsans text-red-500 hover:bg-red-100 hover:border-red-300 transition-colors"
                    >
                      <RotateCcw size={11} /> Erase
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={handleNewConversation}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-semibold font-dmsans text-[#3F57E0] bg-[#EEF1FE] hover:bg-[#e0e5fb] transition-colors border border-[#c7d0f8]"
                >
                  <Plus size={13} /> New chat
                </button>
              </div>
            </div>

            {/* messages */}
            <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-3.5 min-h-0">
              {messages.length === 0 ? (
                /* empty state */
                <div className="flex flex-col items-center justify-center flex-1 text-center py-10">
                  <div
                    className="w-14 h-14 rounded-[16px] flex items-center justify-center mb-5"
                    style={{ background: 'linear-gradient(135deg,#3F57E0,#8B5CF6)' }}
                  >
                    <Sparkles size={24} color="white" />
                  </div>
                  <h2 className="text-[22px] font-bold text-[#0F172A] font-dmsans mb-1.5">How can I help?</h2>
                  <p className="text-[13px] text-[#94A3B8] font-dmsans max-w-xs leading-relaxed mb-6">
                    Ask about roles, referrals, or get a concrete next-step plan. I use your profile context when available.
                  </p>

                  {/* context chips */}
                  {contextChips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 justify-center mb-6 max-w-sm">
                      {contextChips.map((chip) => (
                        <span key={chip} className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-medium font-dmsans border border-[#E6E8EE] text-[#334155] bg-white">
                          {chip}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* quick action grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-sm">
                    {QUICK_ACTIONS.map(({ label, msg }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => handleQuick(msg)}
                        className="text-left px-4 py-3 rounded-xl border border-[#E6E8EE] bg-white text-[12.5px] font-medium font-dmsans text-[#334155] hover:bg-[#F4F5F8] hover:border-[#CBD5E1] transition-colors"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => (
                    <div key={`${msg.timestamp || msg.role}-${idx}`}>
                      {msg.role === 'user'
                        ? <UserBubble>{msg.content}</UserBubble>
                        : <AIBubble><MarkdownLite text={msg.content} /></AIBubble>
                      }

                      {/* job cards */}
                      {msg.metadata?.jobs?.length > 0 && (
                        <div className="mt-2 mb-2 pl-10 flex flex-col gap-2">
                          {msg.metadata.jobs.map((job, ji) => (
                            <div key={job?._id || job?.id || ji} onClick={() => handleJobClick(job)} className="cursor-pointer">
                              <JobResultCard job={job} index={ji} />
                            </div>
                          ))}
                          {msg.metadata.hasMore && (
                            <button
                              type="button"
                              onClick={() => { dispatch(sendMessage({ message: 'Show more', conversationId })); }}
                              className="text-[12.5px] font-semibold font-dmsans text-[#3F57E0] py-2 rounded-lg border border-[#E6E8EE] hover:bg-[#EEF1FE] transition-colors"
                            >
                              Show more jobs
                            </button>
                          )}
                        </div>
                      )}

                      {/* company chips */}
                      {msg.metadata?.companies?.length > 0 && (
                        <div className="mt-2 mb-2 pl-10 flex flex-wrap gap-2">
                          {msg.metadata.companies.slice(0, 6).map((c, ci) => {
                            const name = c?.company || c?.name || c;
                            return (
                              <button
                                key={`${name}-${ci}`}
                                type="button"
                                onClick={() => handleCompanyClick(name, msg.metadata?.suggestionAction)}
                                className="px-3 py-1.5 rounded-full border border-[#E6E8EE] bg-white text-[12.5px] font-medium font-dmsans text-[#3F57E0] hover:bg-[#EEF1FE] transition-colors"
                              >
                                {name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}

                  {isLoading && (
                    streamingText
                      ? (
                        <AIBubble>
                          <MarkdownLite text={streamingText} />
                          <span className="inline-block w-[7px] h-[14px] ml-0.5 align-text-bottom bg-[#3F57E0] animate-pulse rounded-[1px]" />
                        </AIBubble>
                      )
                      : <ThinkingBubble />
                  )}

                  {/* follow-up suggestion */}
                  {!isLoading && followUpQuestion && (
                    <div className="pl-10">
                      <button
                        type="button"
                        onClick={() => handleQuick(followUpQuestion)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#C7D0F8] bg-[#EEF1FE] text-[12px] font-medium font-dmsans text-[#3F57E0] hover:bg-[#e0e5fb] transition-colors text-left"
                      >
                        <Sparkles size={11} /> {followUpQuestion}
                      </button>
                    </div>
                  )}
                </>
              )}
              <div ref={messagesEndRef} className="h-px" />
            </div>

            {/* composer */}
            <Composer
              onSend={handleSend}
              isLoading={isLoading}
              error={error}
              inputRef={inputRef}
              onRetry={lastFailedMessage ? () => handleSend(lastFailedMessage) : null}
            />
          </div>
        </div>
      </div>

      {/* ── History drawer (mobile / tablet) ───────────────────────── */}
      {showHistoryDrawer && (
        <div className="lg:hidden fixed inset-0 z-[150]">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowHistoryDrawer(false)} />
          <div className="absolute top-0 left-0 bottom-0 w-[300px] max-w-[85vw] bg-white shadow-2xl flex flex-col">
            {historyPanelContent}
            <button
              type="button"
              onClick={() => setShowHistoryDrawer(false)}
              className="flex-shrink-0 m-3 py-2 rounded-xl border border-[#E6E8EE] text-[13px] font-semibold text-[#64748b] hover:bg-gray-50 transition-colors font-dmsans"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Delete conversation confirmation modal ─────────────────── */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-6"
          style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)' }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#FEF2F2' }}>
              <Trash2 size={18} className="text-red-500" />
            </div>
            <h3 className="text-[16px] font-bold text-[#0F172A] font-dmsans mb-1">Delete conversation?</h3>
            <p className="text-[13px] text-[#64748B] font-dmsans leading-relaxed mb-1">
              This will permanently remove it from your history:
            </p>
            <p className="text-[12.5px] font-semibold text-[#334155] font-dmsans mb-6 truncate">"{deleteConfirm.title}"</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 text-[13px] font-semibold text-[#334155] border border-[#E6E8EE] rounded-xl bg-white hover:bg-[#F6F7FB] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 text-[13px] font-semibold text-white rounded-xl bg-red-500 hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
